import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef, useState } from 'react'

import Dropzone from 'react-dropzone'
import { Formik, Form, Field, FieldProps } from 'formik'

import './../../../../../components/profile/popups/uploadPhotoPopup/uploadPhoto.sass'
import Popup from '../../../../../components/ui/popup'
import Button from '../../../../../components/ui/button'
import RadioGroupField from '../../../../../components/form/RadioGroupField'
import TextAreaField from '../../../../../components/form/TextAreaField'
import FAQ from '../../../../../components/ui/popup/FAQ'
import { SelectOption } from '../../../../../store/commonTypes'
import { RootState } from '../../../../../store/rootReducer'
import {
    photoUpload,
    photoUploadError,
    photoUploadErrorClean,
    resetPostLoadedCounter
} from '../../../../../store/ephemeral/ephemeralSlice'
import PaginationLoader from '../../../../../components/ui/paginationLoader/PaginationLoader'
import { EphemeralMaterialType, SubmitEphemeral } from '../../../../../store/ephemeral/ephemeralTypings'
import ImagePreview from '../../../../../components/contribution/upload/imagePreview'
import { UploadContributionMaterial } from '../../../../../store/contributions/contributionsTypings'
import axios from 'axios'
import {
    deletePendingEphemeralMaterial,
    getPendingEphemeralList,
    getPendingEphemeralMaterial,
    uploadEphemeralMaterial
} from '../../../../../api/ephemeral'
import TemporaryMap from './TemporaryMap'
import { GeoState } from '../../../../../store/geo/geoTypings'

import 'maplibre-gl/dist/maplibre-gl.css'
import styles from './uploadTemporaryPopup.module.sass'

interface Props {
    onClose: () => void
}

const visibilityOptions: SelectOption[] = [
    {
        value: 'public',
        label: 'Public'
    },
    {
        value: 'pro',
        label: 'Premium'
    }
]

const UploadTemporaryPopup: React.FunctionComponent<Props> = ({ onClose }: Props) => {
    const dispatch = useDispatch()
    const [t] = useTranslation()
    const [uploadingMaterial, setUploadingMaterial] = useState<UploadContributionMaterial | null>(null)
    const [uploadedMaterial, setUploadedMaterial] = useState<EphemeralMaterialType | null>(null)
    const [imageData, setImageData] = useState<string | null>(null)

    const { error } = useSelector((state: RootState) => state.ephemeral)
    const { postLoadedCounter } = useSelector((state: RootState) => state.ephemeral)
    const { postUploading } = useSelector((state: RootState) => state.ephemeral)

    const [stopUploading, setStopUpLoading] = useState(false)

    const [loadingPendingMaterials, setLoadingPendingMaterials] = useState(true)
    const [pendingList, setPendingList] = useState<null | EphemeralMaterialType[]>(null)

    const pendingListTimeoutRef = useRef(null)

    const [shareLocation, setShareLocation] = useState(false)

    const [currentCenter, setCurrentCenter] = useState(null)
    const [currentZoom, setCurrentZoom] = useState(12)

    const [isSharingGeo, setIsSharingGeo] = useState(false)

    useEffect(() => {
        let isMoreLoading = false
        const loadPendingMaterials = async () => {
            try {
                const response = await getPendingEphemeralList()
                if (!response || response.status !== 'success' || !response.list.length) {
                    setLoadingPendingMaterials(false)
                    setPendingList(null)
                    return
                }

                if (response.list.length === 1) {
                    const [item] = response.list
                    const newItem = { ...item }

                    if (item.status !== 'public') {
                        newItem.url = null
                        newItem.thumbnail_url = item.thumbnail_url_pro
                        setUploadedMaterial(newItem)
                        isMoreLoading = true
                    } else {
                        setUploadedMaterial(newItem)
                    }

                    setLoadingPendingMaterials(false)
                } else {
                    const processedList = response.list.map((item) => {
                        if (item.status !== 'public') {
                            isMoreLoading = true
                            return {
                                ...item,
                                url: null,
                                thumbnail_url: item.thumbnail_url_pro
                            }
                        }
                        return item
                    })

                    setPendingList(processedList)
                    setLoadingPendingMaterials(true)
                }
                if (isMoreLoading) {
                    pendingListTimeoutRef.current = setTimeout(loadPendingMaterials, 3000)
                }
            } catch (error) {
                setLoadingPendingMaterials(false)
                console.error('Error loading pending materials:', error)
            }
        }

        setLoadingPendingMaterials(true)
        loadPendingMaterials()

        return () => {
            if (pendingListTimeoutRef.current) {
                clearTimeout(pendingListTimeoutRef.current)
            }
        }
    }, [])

    useEffect(() => {
        if (postLoadedCounter && postLoadedCounter !== 0 && !error) onClose()
        return () => {
            dispatch(resetPostLoadedCounter())
            if (uploadingMaterial) {
                uploadingMaterial.source.cancel()
            }
        }
    }, [postLoadedCounter])

    const timeoutRef = useRef(null)

    const [singleMaterialProgress, setSingleMaterialProgress] = useState(0)

    useEffect(() => {
        let uploadingCounter = 0
        const getPendingVideo = async () => {
            uploadingCounter++
            const videoResponse = await getPendingEphemeralMaterial(uploadedMaterial.id)
            if (videoResponse && videoResponse.contribution && videoResponse.contribution.status === 'public') {
                setStopUpLoading(true)
                setUploadedMaterial(null)
                setUploadedMaterial({
                    id: videoResponse.contribution.id,
                    thumbnail_url: videoResponse.contribution.thumbnail_url,
                    url: videoResponse.contribution.url,
                    //@ts-ignore
                    type: 'video',
                    status: videoResponse.contribution.status,
                    error: null,
                    //@ts-ignore
                    custom: true
                })
            } else {
                if (uploadingCounter < 600) {
                    setSingleMaterialProgress(videoResponse.contribution.processing_progress)
                    if (timeoutRef.current) clearTimeout(timeoutRef.current)
                    timeoutRef.current = setTimeout(() => getPendingVideo(), 3000)
                    // setUploadedMaterial({
                    //     ...videoResponse.contribution
                    // })
                }
            }
        }
        if (uploadedMaterial && uploadedMaterial.status !== 'public' && !stopUploading && !timeoutRef.current)
            getPendingVideo()
        return () => {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }, [uploadedMaterial])

    const handleFileUpload = (files: File[]) => {
        if (files.length === 0) return
        const file = files[0]
        const isVideo = file.type.includes('video')

        if (!isVideo) {
            try {
                const reader = new FileReader()
                reader.onload = (e) => {
                    console.log(e)
                    //@ts-ignore
                    setImageData(e.target.result)
                }
                reader.readAsDataURL(files[0])
                dispatch(photoUploadErrorClean())
            } catch (err) {
                dispatch(photoUploadError())
            } finally {
                // eslint-disable-next-line no-unsafe-finally
                return
            }
            //@ts-ignore
            return
        }

        const source = axios.CancelToken.source()
        const id = Math.random().toString(36).substr(2, 9)

        const newUploadingMaterial: UploadContributionMaterial = {
            file,
            progress: 0,
            source,
            id
        }

        setUploadingMaterial(newUploadingMaterial)
        setUploadedMaterial(null)
        dispatch(photoUploadErrorClean())

        uploadEphemeralMaterial(newUploadingMaterial, (id, progressEvent) => {
            const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100)
            setUploadingMaterial((prev) => (prev ? { ...prev, progress } : null))
        })
            .then((res) => {
                setUploadingMaterial(null)
                setUploadedMaterial({
                    id: res.info.id,
                    thumbnail_url: res.info.thumbnail_url,
                    //@ts-ignore
                    type: 'video',
                    error: null
                })
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    //@ts-ignore
                    dispatch(photoUploadError(err.message))
                    setUploadingMaterial(null)
                }
            })
    }

    const cancelUpload = () => {
        if (uploadingMaterial) {
            uploadingMaterial.source.cancel()
            setUploadingMaterial(null)
        }
    }

    const deleteMaterial = async () => {
        const deleteMaterialResponse = uploadedMaterial
            ? await deletePendingEphemeralMaterial(uploadedMaterial.id)
            : null
        setUploadedMaterial(null)
        setUploadingMaterial(null)
        setImageData(null)
        setStopUpLoading(false)
        setSingleMaterialProgress(0)
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
    }

    const deleteMaterialFromList = async (id: number) => {
        const deleteMaterialResponse = await deletePendingEphemeralMaterial(id)
        if (deleteMaterialResponse.status === 'success') {
            if (pendingList.length === 2) {
                setLoadingPendingMaterials(false)
                setUploadedMaterial(pendingList.filter((material) => material.id !== id)[0])
            }
            setPendingList((prev) => prev.filter((material) => material.id !== id))
        }
    }

    return (
        <Popup title={t('Post a ephemeral story')} classname={styles['popup']} onClose={onClose} profile={true}>
            <FAQ title={t('Terms of use')} isRed>
                <>
                    <p>{t('t_photo_faq_1')}</p>
                    <p>{t('t_photo_faq_2')}</p>
                    <p>{t('t_photo_faq_3')}</p>
                    <p>{t('t_photo_faq_4')}</p>
                    <p>{t('t_photo_faq_5')}</p>
                    <p>{t('t_photo_faq_6')}</p>
                </>
            </FAQ>
            {error && (
                <p className="dropzone__error" style={{ marginTop: '15px' }}>
                    {error}
                </p>
            )}
            {postUploading ? (
                <div
                    style={{
                        width: '100%',
                        height: '500px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <PaginationLoader />
                </div>
            ) : (
                <Formik
                    initialValues={{
                        status: 'public',
                        content: ''
                    }}
                    onSubmit={(values: Omit<SubmitEphemeral, 'image_data'>, formikHelpers) => {
                        if (uploadedMaterial || imageData) {
                            const dataToSend = {}

                            if (isSharingGeo && currentCenter) {
                                //@ts-ignore
                                dataToSend.lat = currentCenter.lat
                                //@ts-ignore
                                dataToSend.lon = currentCenter.lon
                                //@ts-ignore
                                dataToSend.zoom = currentZoom
                            }

                            //@ts-ignore
                            if (imageData) dataToSend.image_data = imageData
                            //@ts-ignore
                            if (uploadedMaterial && uploadedMaterial.id) dataToSend.video_id = uploadedMaterial.id

                            dispatch(
                                photoUpload({
                                    ...values,
                                    ...dataToSend
                                })
                            )

                            formikHelpers.resetForm()
                        }
                    }}
                >
                    {(props) => (
                        <Form className="form">
                            {loadingPendingMaterials ? (
                                <div className="preview preview_ephemeral preview_ephemeral_list">
                                    {pendingList && pendingList.length ? (
                                        <p style={{ color: '#a23330', fontSize: '12px' }}>
                                            {t(
                                                'Please remove any unsuitable videos - keep just one or delete them all before continuing'
                                            )}
                                        </p>
                                    ) : (
                                        <></>
                                    )}
                                    {pendingList && pendingList.length ? (
                                        pendingList.map((material) => (
                                            <div key={material.id}>
                                                <ImagePreview
                                                    material={material}
                                                    uploadingMaterial={uploadingMaterial}
                                                    cancel={cancelUpload}
                                                    deleteMaterial={() => deleteMaterialFromList(material.id)}
                                                />
                                                <div
                                                    className={'controlButton_ephemeral'}
                                                    onClick={() => deleteMaterialFromList(material.id)}
                                                />
                                            </div>
                                        ))
                                    ) : (
                                        <PaginationLoader />
                                    )}
                                </div>
                            ) : (
                                <>
                                    {!uploadingMaterial && !uploadedMaterial && !imageData ? (
                                        <Dropzone
                                            accept={['image/*', 'video/*']}
                                            multiple={false}
                                            onDrop={handleFileUpload}
                                        >
                                            {({ getRootProps, getInputProps }) => (
                                                <div
                                                    {...getRootProps()}
                                                    className="dropzone"
                                                    style={{ marginTop: '20px' }}
                                                >
                                                    <input {...getInputProps()} />
                                                    <p>{t('Drag your file here')}</p>
                                                    <Button type="button" label={t('Or add from')} />
                                                </div>
                                            )}
                                        </Dropzone>
                                    ) : (
                                        <div className="preview preview_ephemeral">
                                            {imageData ? (
                                                <>
                                                    <img src={imageData} alt="" style={{ marginTop: '15px' }} />
                                                    <div
                                                        className={'controlButton_ephemeral'}
                                                        onClick={deleteMaterial}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <ImagePreview
                                                        progress={singleMaterialProgress}
                                                        material={uploadedMaterial}
                                                        uploadingMaterial={uploadingMaterial}
                                                        cancel={cancelUpload}
                                                        deleteMaterial={deleteMaterial}
                                                    />
                                                    {uploadedMaterial && (
                                                        <div
                                                            className={'controlButton_ephemeral'}
                                                            onClick={deleteMaterial}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            <Field name="status">
                                {({ field, form }: FieldProps) => (
                                    <RadioGroupField
                                        form={form}
                                        field={field}
                                        label={t('Upload in')}
                                        options={visibilityOptions}
                                    />
                                )}
                            </Field>

                            {/*<div style={{ margin: '20px 0' }}>*/}
                            {/*    {!shareLocation && !currentCenter && (*/}
                            {/*        <Button label={'Show my location'} onClick={() => setShareLocation(true)} />*/}
                            {/*    )}*/}

                            {/*    {isSharingGeo && currentCenter && (*/}
                            {/*        <div*/}
                            {/*            style={{*/}
                            {/*                display: 'flex',*/}
                            {/*                flexDirection: 'column',*/}
                            {/*                gap: '5px',*/}
                            {/*                marginBottom: shareLocation ? '5px' : 0*/}
                            {/*            }}*/}
                            {/*        >*/}
                            {/*            <p style={{ color: '#00ac00' }}>*/}
                            {/*                You’ve selected a location to share with other users. You can edit or delete*/}
                            {/*                it.*/}
                            {/*            </p>*/}
                            {/*            {!shareLocation && (*/}
                            {/*                <div style={{ display: 'flex', gap: '5px' }}>*/}
                            {/*                    {' '}*/}
                            {/*                    <Button*/}
                            {/*                        label={'Edit'}*/}
                            {/*                        onClick={() => {*/}
                            {/*                            setShareLocation(true)*/}
                            {/*                        }}*/}
                            {/*                    />*/}
                            {/*                    <Button*/}
                            {/*                        label={'Delete'}*/}
                            {/*                        secondary*/}
                            {/*                        onClick={() => {*/}
                            {/*                            setCurrentCenter(null)*/}
                            {/*                            setCurrentZoom(null)*/}
                            {/*                            setShareLocation(false)*/}
                            {/*                            setCurrentZoom(12)*/}
                            {/*                            setIsSharingGeo(false)*/}
                            {/*                        }}*/}
                            {/*                    />*/}
                            {/*                </div>*/}
                            {/*            )}*/}
                            {/*        </div>*/}
                            {/*    )}*/}
                            {/*    {shareLocation && (*/}
                            {/*        <div*/}
                            {/*            style={{*/}
                            {/*                // backgroundColor: 'var(--main-theme-transparent-color)',*/}
                            {/*                display: 'flex',*/}
                            {/*                flexDirection: 'column',*/}
                            {/*                gap: '10px'*/}
                            {/*            }}*/}
                            {/*        >*/}
                            {/*            <div style={{ display: 'flex', gap: '10px', margin: '0 0 10px' }}>*/}
                            {/*                <Button*/}
                            {/*                    label={'Share'}*/}
                            {/*                    onClick={() => {*/}
                            {/*                        setShareLocation(false)*/}
                            {/*                        setIsSharingGeo(true)*/}
                            {/*                    }}*/}
                            {/*                />*/}
                            {/*                <Button*/}
                            {/*                    label={'Not share'}*/}
                            {/*                    secondary*/}
                            {/*                    onClick={() => {*/}
                            {/*                        setCurrentCenter(null)*/}
                            {/*                        setCurrentZoom(null)*/}
                            {/*                        setShareLocation(false)*/}
                            {/*                        setCurrentZoom(12)*/}
                            {/*                        setIsSharingGeo(false)*/}
                            {/*                    }}*/}
                            {/*                />*/}
                            {/*            </div>*/}
                            {/*            <div>*/}
                            {/*                <TemporaryMap*/}
                            {/*                    currentCenter={currentCenter}*/}
                            {/*                    currentZoom={currentZoom}*/}
                            {/*                    setCurrentCenter={setCurrentCenter}*/}
                            {/*                    setCurrentZoom={setCurrentZoom}*/}
                            {/*                />*/}
                            {/*            </div>*/}
                            {/*        </div>*/}
                            {/*    )}*/}
                            {/*</div>*/}

                            <Field name="content">
                                {({ field, meta, form }: FieldProps) => (
                                    <TextAreaField
                                        form={form}
                                        field={field}
                                        error={meta.error}
                                        label={t('Description')}
                                        placeholder={t('Description of the story with an uncovered face')}
                                    />
                                )}
                            </Field>
                            {(uploadingMaterial || uploadedMaterial || imageData) && (
                                <div className="preview">
                                    <div className="uploadControls">
                                        <Button
                                            label={t('Add')}
                                            onClick={props.submitForm}
                                            type="button"
                                            disabled={
                                                !!uploadingMaterial ||
                                                (uploadedMaterial &&
                                                    uploadedMaterial.type === 'video' &&
                                                    !uploadedMaterial.url)
                                            }
                                        />
                                        <Button
                                            tertiary
                                            type="button"
                                            label={t('Reset')}
                                            onClick={uploadedMaterial ? deleteMaterial : cancelUpload}
                                        />
                                    </div>
                                </div>
                            )}
                        </Form>
                    )}
                </Formik>
            )}
        </Popup>
    )
}

export default UploadTemporaryPopup
